import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Close when clicking the backdrop (default true). */
  dismissOnBackdrop?: boolean;
  size?: 'sm' | 'md';
  /** Selector of the element to focus when opening (default: first field, else the panel). */
  initialFocus?: string;
  icon?: ReactNode;
}

/**
 * Modal dialog on top of the native <dialog> element: `showModal()` makes the
 * rest of the page inert (focus stays inside), Esc closes, focus returns to
 * the element that opened it.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  dismissOnBackdrop = true,
  size = 'md',
  initialFocus,
  icon,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
      const target =
        (initialFocus ? dialog.querySelector<HTMLElement>(initialFocus) : null) ??
        dialog.querySelector<HTMLElement>('[data-autofocus]') ??
        dialog.querySelector<HTMLElement>('input:not([type=hidden]), textarea, select') ??
        dialog.querySelector<HTMLElement>('.dialog__body');
      target?.focus({ preventScroll: true });
    } else if (!open && dialog.open) {
      dialog.close();
      const back = returnFocus.current;
      if (back?.isConnected) back.focus({ preventScroll: true });
    }
  }, [open, initialFocus]);

  useEffect(() => () => ref.current?.close(), []);

  return (
    <dialog
      ref={ref}
      className={`dialog dialog--${size}`}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onCloseRef.current();
      }}
      onMouseDown={(event) => {
        if (!dismissOnBackdrop || event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) onCloseRef.current();
      }}
    >
      {open && (
        <div className="dialog__inner">
          <header className="dialog__header">
            {icon && <span className="dialog__icon" aria-hidden="true">{icon}</span>}
            <div className="dialog__heading">
              <h2 id={titleId} className="dialog__title">
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className="dialog__description">
                  {description}
                </p>
              )}
            </div>
            <button type="button" className="icon-btn dialog__close" onClick={onClose} aria-label="Schließen">
              <X size={20} aria-hidden="true" />
            </button>
          </header>
          <div className="dialog__body" tabIndex={-1}>
            {children}
          </div>
        </div>
      )}
    </dialog>
  );
}
